import {
  Component,
  ChangeDetectionStrategy,
  input,
  output,
  signal,
  computed,
  effect,
  OnInit
} from '@angular/core';
import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay';
import { TranslocoPipe } from '@jsverse/transloco';
import { MatIcon } from '@angular/material/icon';
import { Constraint, PolicyType, PolicyRole, UseCaseId, region } from '@htx/policies';
import { ConstraintPaletteComponent } from '../constraint-palette/constraint-palette.component';
import { ConstraintChipComponent } from '../constraint-chip/constraint-chip.component';
import { ConstraintPopoverComponent } from '../constraint-popover/constraint-popover.component';
import { isConstraintConfigured, isMandatoryConstraint } from '../../shared/constraint-metadata';

/** Internal entry with stable tracking id. */
interface ConstraintEntry {
  trackId: string;
  constraint: Constraint;
}

let nextId = 0;

@Component({
  selector: 'app-policy-builder',
  imports: [
    CdkConnectedOverlay,
    TranslocoPipe,
    MatIcon,
    ConstraintPaletteComponent,
    ConstraintChipComponent,
    ConstraintPopoverComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './policy-builder.component.html',
  styleUrl: './policy-builder.component.scss'
})
export class PolicyBuilderComponent implements OnInit {
  readonly initialConstraints = input<Constraint[]>([]);
  readonly compact = input(false);
  readonly role = input<PolicyRole | undefined>(undefined);
  readonly constraintsChange = output<Constraint[]>();
  readonly validChange = output<boolean>();

  readonly entries = signal<ConstraintEntry[]>([]);

  readonly usedTypes = computed<PolicyType[]>(() =>
    this.entries().map(e => e.constraint.type)
  );

  readonly isEmpty = computed(() => this.entries().length === 0);

  // ─── Overlay State ──────────────────────────────────────
  readonly overlayOpen = signal(false);
  readonly overlayOrigin = signal<CdkOverlayOrigin | null>(null);
  readonly editingEntry = signal<ConstraintEntry | null>(null);

  readonly overlayPositions: ConnectedPosition[] = [
    // Primary: above the chip, growing upward
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -6 },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -6 },
    // Fallback: below (only if no space above)
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 6 }
  ];

  /** Expose helpers to template. */
  readonly isConfigured = isConstraintConfigured;
  readonly isStatic = (c: Constraint) =>
    c.type === PolicyType.MEMBERSHIP_STATIC || c.type === PolicyType.FRAMEWORK_AGREEMENT;

  readonly isLocked = (c: Constraint) => isMandatoryConstraint(c.type, this.role());

  /** Lookup: trackId → ConstraintChipComponent (populated after view renders). */
  private readonly chipRefs = new Map<string, ConstraintChipComponent>();

  /** Guard: prevent the effect from emitting before ngOnInit has read initialConstraints. */
  private readonly initialized = signal(false);

  constructor() {
    effect(() => {
      const ready = this.initialized();
      const constraints = this.entries().map(e => e.constraint);
      if (!ready) return;
      this.constraintsChange.emit(constraints);
      const allConfigured = constraints.length === 0 || constraints.every(isConstraintConfigured);
      this.validChange.emit(allConfigured);
    });
  }

  ngOnInit(): void {
    const initial = this.initialConstraints();
    if (initial.length > 0) {
      this.entries.set(
        initial.map(c => ({ trackId: `cc-${nextId++}`, constraint: c }))
      );
    }
    this.initialized.set(true);
  }

  /** Register a chip reference (called from template). */
  registerChip(trackId: string, chip: ConstraintChipComponent): void {
    this.chipRefs.set(trackId, chip);
  }

  /** Add a new constraint and auto-open popover for configuration. */
  addConstraint(type: PolicyType): void {
    if (type !== PolicyType.UNKNOWN && this.usedTypes().includes(type)) return;

    const constraint = this.createDefault(type);
    if (!constraint) return;

    const entry: ConstraintEntry = { trackId: `cc-${nextId++}`, constraint };
    this.entries.update(entries => [...entries, entry]);

    // Auto-open popover after DOM renders (except for static types)
    if (type !== PolicyType.MEMBERSHIP_STATIC && type !== PolicyType.FRAMEWORK_AGREEMENT) {
      setTimeout(() => {
        const chip = this.chipRefs.get(entry.trackId);
        if (chip) {
          this.openPopover(entry, chip.chipOrigin());
        }
      });
    }
  }

  /** Open popover anchored to a chip's overlay origin. */
  openPopover(entry: ConstraintEntry, origin: CdkOverlayOrigin): void {
    this.editingEntry.set(entry);
    this.overlayOrigin.set(origin);
    this.overlayOpen.set(true);
  }

  /** Handle chip edit event (receives the origin from the chip). */
  onChipEdit(entry: ConstraintEntry, origin: CdkOverlayOrigin): void {
    // Static types have nothing to configure — skip the popover
    if (entry.constraint.type === PolicyType.MEMBERSHIP_STATIC ||
        entry.constraint.type === PolicyType.FRAMEWORK_AGREEMENT) {
      return;
    }
    this.openPopover(entry, origin);
  }

  closePopover(): void {
    this.overlayOpen.set(false);
    this.editingEntry.set(null);
    this.overlayOrigin.set(null);
  }

  onPopoverApply(updated: Constraint): void {
    const entry = this.editingEntry();
    if (entry) {
      this.updateConstraint(entry.trackId, updated);
    }
  }

  updateConstraint(trackId: string, updated: Constraint): void {
    this.entries.update(entries =>
      entries.map(e => e.trackId === trackId ? { ...e, constraint: updated } : e)
    );
  }

  removeConstraint(trackId: string): void {
    const entry = this.entries().find(e => e.trackId === trackId);
    if (entry && isMandatoryConstraint(entry.constraint.type, this.role())) return;

    if (this.editingEntry()?.trackId === trackId) {
      this.closePopover();
    }
    this.chipRefs.delete(trackId);
    this.entries.update(entries => entries.filter(e => e.trackId !== trackId));
  }

  getConstraints(): Constraint[] {
    return this.entries().map(e => e.constraint);
  }

  isEditing(trackId: string): boolean {
    return this.editingEntry()?.trackId === trackId;
  }

  private createDefault(type: PolicyType): Constraint | null {
    switch (type) {
      case PolicyType.PARTICIPANT_RESTRICTED:
        return { type, bpnNumbers: [] };
      case PolicyType.MEMBERSHIP_STATIC:
      case PolicyType.FRAMEWORK_AGREEMENT:
        return { type };
      case PolicyType.USE_CASE_MEMBERSHIP:
        return { type, useCaseId: '' as UseCaseId };
      case PolicyType.TIME_PERIOD_RESTRICTED:
        return { type, count: 0 };
      case PolicyType.END_DATE:
        return { type, endDate: new Date() };
      case PolicyType.COMPANY_REGION_RESTRICTED:
        return { type, region: '' as region };
      case PolicyType.DATACENTER_REGION_RESTRICTED:
        return { type, region: '' as region };
      default:
        return null;
    }
  }
}
